import { useState, useRef, useEffect, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useAnnotationStore, type Tool } from '../../stores/annotationStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { PDFManipulator } from '../../core/PDFManipulator';
import { pdfEngine } from '../../core/PDFEngine';
import { downloadUint8Array, uint8ArrayToArrayBuffer } from '../../utils/fileUtils';
import './Toolbar.css';

interface ToolbarProps {
  onOpenFile: () => void;
}

export function Toolbar({ onOpenFile }: ToolbarProps) {
  const { activeDocumentId, documents, zoom, zoomIn, zoomOut, setZoom, rotation, rotateClockwise } = useDocumentStore();
  const { activeTool, setActiveTool } = useAnnotationStore();
  const { toggleSidebar, toggleTheme, theme, openDialog, toggleSearchPanel } = useUIStore();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const hasDocument = !!activeDoc;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSave = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    try {
      const overlays = await useAnnotationStore.getState().exportAllPageOverlays();
      let pdfBytes: Uint8Array;

      if (overlays.length > 0) {
        pdfBytes = await PDFManipulator.addOverlaysToPages(activeDoc.arrayBuffer, overlays);
      } else {
        pdfBytes = new Uint8Array(activeDoc.arrayBuffer);
      }

      downloadUint8Array(pdfBytes, activeDoc.filename);
      useUIStore.getState().addToast({ type: 'success', message: 'File saved with annotations!' });
    } catch (err: any) {
      console.error('Save failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Failed to save document' });
    }
  }, [activeDoc]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const toolGroups: { label: string; tools: { tool: Tool; icon: string; label: string }[] }[] = [
    {
      label: 'Selection',
      tools: [
        { tool: 'select', icon: '⊹', label: 'Select (V)' },
        { tool: 'hand', icon: '✋', label: 'Hand Tool (H)' },
        { tool: 'text-select', icon: 'T̲', label: 'Text Select' },
      ],
    },
    {
      label: 'Markup',
      tools: [
        { tool: 'highlight', icon: '🖍️', label: 'Highlight' },
        { tool: 'underline', icon: 'U̲', label: 'Underline' },
        { tool: 'strikethrough', icon: 'S̶', label: 'Strikethrough' },
      ],
    },
    {
      label: 'Annotate',
      tools: [
        { tool: 'text-annotation', icon: 'Aa', label: 'Text Box' },
        { tool: 'sticky-note', icon: '📝', label: 'Sticky Note' },
        { tool: 'stamp', icon: '🔖', label: 'Stamp' },
        { tool: 'signature', icon: '✍️', label: 'Signature' },
      ],
    },
    {
      label: 'Draw',
      tools: [
        { tool: 'freehand', icon: '✏️', label: 'Pencil' },
        { tool: 'rectangle', icon: '▭', label: 'Rectangle' },
        { tool: 'circle', icon: '○', label: 'Circle' },
        { tool: 'arrow', icon: '→', label: 'Arrow' },
        { tool: 'line', icon: '╱', label: 'Line' },
      ],
    },
    {
      label: 'Tools',
      tools: [
        { tool: 'redact', icon: '█', label: 'Redact' },
        { tool: 'eraser', icon: '🧹', label: 'Eraser' },
        { tool: 'measure-distance', icon: '📏', label: 'Measure' },
      ],
    },
  ];

  const handleInsertBlankPage = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    const { currentPage, totalPages } = useDocumentStore.getState();

    try {
      useDocumentStore.getState().pushDocHistory(activeDoc.id, 'Insert Blank Page');
      const result = await PDFManipulator.addBlankPage(activeDoc.arrayBuffer, currentPage);
      const newBuffer = uint8ArrayToArrayBuffer(result);

      await pdfEngine.loadDocument(newBuffer);
      useDocumentStore.getState().updateDocument(activeDoc.id, {
        arrayBuffer: newBuffer,
        pageCount: totalPages + 1,
        lastModified: Date.now(),
      });
      useDocumentStore.getState().setTotalPages(totalPages + 1);
      useDocumentStore.getState().setCurrentPage(currentPage + 1);
      useUIStore.getState().addToast({ type: 'success', message: 'Blank page inserted!' });
    } catch (err: any) {
      console.error('Insert blank page failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: `Failed to insert blank page: ${err?.message || ''}` });
    }
  }, [activeDoc]);

  const handleDeleteCurrentPage = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    const { currentPage, totalPages } = useDocumentStore.getState();
    if (totalPages <= 1) {
      useUIStore.getState().addToast({ type: 'warning', message: 'Cannot delete the only page' });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete page ${currentPage}?`)) return;

    try {
      useDocumentStore.getState().pushDocHistory(activeDoc.id, `Delete Page ${currentPage}`);
      const result = await PDFManipulator.deletePages(activeDoc.arrayBuffer, [currentPage]);
      const newBuffer = uint8ArrayToArrayBuffer(result);

      await pdfEngine.loadDocument(newBuffer.slice(0));
      const newTotal = totalPages - 1;
      useDocumentStore.getState().updateDocument(activeDoc.id, {
        arrayBuffer: newBuffer,
        pageCount: newTotal,
        lastModified: Date.now(),
      });
      useDocumentStore.getState().setTotalPages(newTotal);
      useDocumentStore.getState().setCurrentPage(Math.min(currentPage, newTotal));
      useUIStore.getState().addToast({ type: 'success', message: `Page ${currentPage} deleted!` });
    } catch (err: any) {
      console.error('Delete page failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: `Failed to delete page: ${err?.message || ''}` });
    }
  }, [activeDoc]);

  return (
    <div className="toolbar" ref={menuRef}>
      {/* Left: Menu buttons */}
      <div className="toolbar-section toolbar-menus">
        <div className="toolbar-menu-group">
          <button
            className={`toolbar-menu-btn ${openMenu === 'file' ? 'active' : ''}`}
            onClick={() => setOpenMenu(openMenu === 'file' ? null : 'file')}
          >
            File
          </button>
          {openMenu === 'file' && (
            <div className="dropdown toolbar-dropdown">
              <button className="dropdown-item" onClick={() => { onOpenFile(); setOpenMenu(null); }}>
                <span>📂</span> Open <span className="shortcut">Ctrl+O</span>
              </button>
              <button className="dropdown-item" onClick={() => { handleSave(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>💾</span> Save As <span className="shortcut">Ctrl+S</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { openDialog('merge'); setOpenMenu(null); }}>
                <span>📎</span> Merge PDFs
              </button>
              <button className="dropdown-item" onClick={() => { openDialog('split'); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>✂️</span> Split PDF
              </button>
              <button className="dropdown-item" onClick={() => { openDialog('compress'); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>📦</span> Compress
              </button>
              <button className="dropdown-item" onClick={() => { openDialog('convert'); setOpenMenu(null); }}>
                <span>🔄</span> Convert
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { handlePrint(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>🖨️</span> Print <span className="shortcut">Ctrl+P</span>
              </button>
            </div>
          )}
        </div>

        <div className="toolbar-menu-group">
          <button
            className={`toolbar-menu-btn ${openMenu === 'view' ? 'active' : ''}`}
            onClick={() => setOpenMenu(openMenu === 'view' ? null : 'view')}
          >
            View
          </button>
          {openMenu === 'view' && (
            <div className="dropdown toolbar-dropdown">
              <button className="dropdown-item" onClick={() => { toggleSidebar(); setOpenMenu(null); }}>
                <span>📑</span> Toggle Sidebar
              </button>
              <button className="dropdown-item" onClick={() => { toggleSearchPanel(); setOpenMenu(null); }}>
                <span>🔍</span> Find <span className="shortcut">Ctrl+F</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setZoom(1); setOpenMenu(null); }}>
                <span>🔲</span> Actual Size <span className="shortcut">Ctrl+0</span>
              </button>
              <button className="dropdown-item" onClick={() => { zoomIn(); setOpenMenu(null); }}>
                <span>🔍</span> Zoom In <span className="shortcut">Ctrl++</span>
              </button>
              <button className="dropdown-item" onClick={() => { zoomOut(); setOpenMenu(null); }}>
                <span>🔍</span> Zoom Out <span className="shortcut">Ctrl+-</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { rotateClockwise(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>🔄</span> Rotate Clockwise
              </button>
              <button className="dropdown-item" onClick={() => { toggleTheme(); setOpenMenu(null); }}>
                <span>{theme === 'dark' ? '☀️' : '🌙'}</span> {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          )}
        </div>

        <div className="toolbar-menu-group">
          <button
            className={`toolbar-menu-btn ${openMenu === 'edit' ? 'active' : ''}`}
            onClick={() => setOpenMenu(openMenu === 'edit' ? null : 'edit')}
          >
            Edit
          </button>
          {openMenu === 'edit' && (
            <div className="dropdown toolbar-dropdown">
              <button className="dropdown-item" onClick={() => { useAnnotationStore.getState().undo(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>↩️</span> Undo <span className="shortcut">Ctrl+Z</span>
              </button>
              <button className="dropdown-item" onClick={() => { useAnnotationStore.getState().redo(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>↪️</span> Redo <span className="shortcut">Ctrl+Y</span>
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { openDialog('pageOrganizer'); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>📄</span> Organize / Reorder Pages
              </button>
              <button className="dropdown-item" onClick={() => { handleInsertBlankPage(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>➕</span> Insert Blank Page
              </button>
              <button className="dropdown-item" onClick={() => { handleDeleteCurrentPage(); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>🗑️</span> Delete Current Page
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { openDialog('ocr'); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>🔍</span> OCR — Extract Text
              </button>
              <button className="dropdown-item" onClick={() => { openDialog('properties'); setOpenMenu(null); }} disabled={!hasDocument}>
                <span>ℹ️</span> Document Properties
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="divider" />

      {/* Center: Tool buttons */}
      <div className="toolbar-section toolbar-tools">
        {hasDocument && (
          <div className="tool-group" style={{ display: 'flex', gap: '2px', marginRight: '6px' }}>
            <button
              className="toolbar-tool-btn"
              onClick={() => useAnnotationStore.getState().undo()}
              title="Undo (Ctrl+Z)"
            >
              ↩️
            </button>
            <button
              className="toolbar-tool-btn"
              onClick={() => useAnnotationStore.getState().redo()}
              title="Redo (Ctrl+Y)"
            >
              ↪️
            </button>
          </div>
        )}

        {hasDocument && toolGroups.map((group, gi) => (
          <div key={group.label} className="tool-group">
            {gi > 0 && <div className="divider" />}
            {group.tools.map((t) => (
              <button
                key={t.tool}
                className={`toolbar-tool-btn ${activeTool === t.tool ? 'active' : ''}`}
                onClick={() => setActiveTool(t.tool)}
                title={t.label}
              >
                <span className="tool-icon">{t.icon}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Right: Zoom & utilities */}
      <div className="toolbar-section toolbar-right">
        {hasDocument && (
          <div className="zoom-controls">
            <button className="btn btn-ghost btn-icon" onClick={zoomOut} title="Zoom Out">
              −
            </button>
            <span className="zoom-label">{Math.round(zoom * 100)}%</span>
            <button className="btn btn-ghost btn-icon" onClick={zoomIn} title="Zoom In">
              +
            </button>
          </div>
        )}
        <button
          className="btn btn-ghost btn-icon"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        
        {user?.role === 'admin' && (
          <button 
            className="toolbar-menu-btn" 
            title="Admin Settings"
            onClick={() => openDialog('admin')}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
          >
            <span>⚙️</span>
          </button>
        )}
        
        {isAuthenticated && user ? (
          <div className="toolbar-menu-group">
            <button
              className={`toolbar-menu-btn ${openMenu === 'account' ? 'active' : ''}`}
              onClick={() => setOpenMenu(openMenu === 'account' ? null : 'account')}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <span style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                background: user.role === 'admin' ? 'var(--color-primary)' : '#4ade80',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px'
              }}>
                {user.role === 'admin' ? 'A' : 'U'}
              </span>
              <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
            </button>
            {openMenu === 'account' && (
              <div className="dropdown toolbar-dropdown" style={{ right: 0, left: 'auto' }}>
                <div className="dropdown-item" style={{ cursor: 'default' }}>
                  <div style={{ fontSize: 'var(--text-sm)', textTransform: 'capitalize' }}>Role: {user.role}</div>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={() => { logout(); setOpenMenu(null); }}>
                  <span>🚪</span> Sign Out
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
